import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import VerificationCodeInput from '../../shared/components/VerificationCodeInput';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import signupImage from '../../assets/images/signup.png';
import { AUTH_STACK } from '../../utils/screenNames';
import {
  verifyAccount,
  activateTemporaryAcc,
  resendVerificationCode,
} from '../../redux/actions/authActions';
import { getUser, updateUserInfo } from '../../redux/slices/userSlice';
import { loginSuccess } from '../../redux/slices/authSlice';
import {
  LOCAL_STORAGE_KEYS,
  setLocalStorageItem,
} from '../../utils/localStorage';
import { identifyUser, registerCompleted } from '../../utils/analytics';

/**
 * SignupVerification Page - Refactored with Tailwind CSS
 * User verifies email with 6-digit code
 */
function SignupVerification() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const registrationInfo = location.state?.registrationInfo;
  const { email } = location?.state || {};

  const user = useSelector(getUser);

  const [verificationCode, setVerificationCode] = useState(new Array(6).fill(''));
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [timer, setTimer] = useState(0);

  /**
   * Handle resend timer
   */
  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }

    return () => clearInterval(countdown);
  }, [timer]);

  /**
   * Handle code verification
   */
  const handleVerify = async () => {
    const confirmationCode = verificationCode.join('');

    if (confirmationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setError('');

    try {
      if (user.tokens) {
        await verifyAccount(
          confirmationCode,
          user,
          (verifiedUser) => {
            const { access, refresh } = verifiedUser.tokens;

            setLocalStorageItem(
              LOCAL_STORAGE_KEYS.USERNAME,
              verifiedUser?.user?.profile?.fullName ?? ''
            );
            setLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS, access);
            setLocalStorageItem(LOCAL_STORAGE_KEYS.REFRESH, refresh);

            dispatch(updateUserInfo({ userData: verifiedUser?.user }));
            dispatch(loginSuccess({ access, isAuthenticated: true }));

            identifyUser(verifiedUser?.user);
            registerCompleted({ signupMethod: 'email', user: verifiedUser?.user });

            toast.success('Signup successful');
            navigate(AUTH_STACK.PERSONAL_DETAILS, {
              state: { email: email },
            });
          },
          () => {
            setError('Verification failed');
          }
        );
      } else {
        await activateTemporaryAcc(
          confirmationCode,
          registrationInfo,
          (verifiedUser) => {
            const { access, refresh } = verifiedUser.tokens;

            setLocalStorageItem(
              LOCAL_STORAGE_KEYS.USERNAME,
              verifiedUser?.user?.profile?.fullName ?? ''
            );
            setLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS, access);
            setLocalStorageItem(LOCAL_STORAGE_KEYS.REFRESH, refresh);

            dispatch(updateUserInfo({ userData: verifiedUser?.user }));
            dispatch(loginSuccess({ access, isAuthenticated: true }));

            identifyUser(verifiedUser?.user);
            registerCompleted({ signupMethod: 'email', user: verifiedUser?.user });

            toast.success('Signup successful');
            navigate(AUTH_STACK.PERSONAL_DETAILS, {
              state: { email: email },
            });
          },
          () => {
            setError('Verification failed');
          }
        );
      }
    } catch (e) {
      setError('Verification failed (unexpected)');
    }
  };

  /**
   * Handle resend code
   */
  const handleResendCode = async () => {
    setIsResendDisabled(true);
    setTimer(15);
    setResendLoading(true);

    await resendVerificationCode(
      registrationInfo,
      user,
      () => {
        setResendLoading(false);
        toast.success('Code resent successfully');
      },
      () => {
        setResendLoading(false);
        toast.error('Failed to resend code');
      }
    );
  };

  if (resendLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-bg-dark">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-shaded-text hover:text-[#FEFCEB] transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-proxima text-sm">Back to log in</span>
      </button>

      {/* Main Content */}
      <AuthLayout
        heroImage={signupImage}
        title="Check your inbox"
        subtitle={
          <>
            Enter the verification code we just sent to
            <br />
            <span className="text-clubbie-blue font-medium">{email}</span>
          </>
        }
      >
        {/* Verification Code Input */}
        <div className="w-full flex justify-center my-8">
          <VerificationCodeInput
            length={6}
            value={verificationCode}
            onChange={setVerificationCode}
            error={error}
          />
        </div>

        {/* Resend Section */}
        <div className="w-full text-center mb-4">
          <p className="text-sm text-shaded-text font-proxima">
            Didn't receive the code?{' '}
            <button
              onClick={handleResendCode}
              disabled={isResendDisabled}
              className={`font-medium ${
                isResendDisabled
                  ? 'text-shaded-text cursor-not-allowed'
                  : 'text-[#FEFCEB] hover:text-clubbie-blue transition-colors'
              }`}
            >
              Resend
            </button>
          </p>
          {isResendDisabled && (
            <p className="text-xs text-shaded-text mt-1 font-proxima">
              Resend code again in {timer} seconds
            </p>
          )}
        </div>

        {/* Verify Button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleVerify}
              className="flex-1 w-full h-11 bg-[#00E7FF] mt-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity"
          disabled={verificationCode.some((code) => code === '')}
        >
          Confirm
        </Button>

        {/* Back to Login Link */}
        <button
          onClick={() => navigate(-1)}
          className="text-center text-sm text-shaded-text hover:text-light-blue transition-colors font-proxima mt-4"
        >
          Back to log in
        </button>
      </AuthLayout>
    </div>
  );
}

export default SignupVerification;
